import React from "react";
import { Stack, Box, Typography, Button } from "@mui/material";
import { useScrollToSection } from "~/single-page/hooks/useScrollToSection";
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import DescriptionIcon from '@mui/icons-material/Description';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import HandshakeIcon from '@mui/icons-material/Handshake';
import LaunchIcon from '@mui/icons-material/Launch';

const scrollMarginStyle = {
  scrollMarginTop: '100px'
};

export default function ActiveNoticesPage() {
  useScrollToSection();

  return (
    <Stack spacing={6} sx={{ maxWidth: '100%' }}>
      {/* Header */}
      <Box id="about-notices" sx={scrollMarginStyle}>
        <Typography variant="h4" component="h2" sx={{ 
          fontWeight: 'bold', 
          mb: 3,
          mt: 2,
          color: '#1B4B72',
          textAlign: 'center'
        }}>
          Editais Ativos
        </Typography>
        <Typography variant="body1" sx={{ 
          maxWidth: '800px', 
          mx: 'auto',
          mb: 3
        }}>
          Os editais são instrumentos essenciais para a captação de recursos e fortalecimento 
          de projetos sociais. São chamadas públicas que oferecem oportunidades de financiamento 
          para organizações que atendem critérios específicos.
        </Typography>
        <Typography variant="body1" sx={{ 
          maxWidth: '800px', 
          mx: 'auto'
        }}>
          Participar de editais é uma forma estruturada de obter recursos financeiros, 
          técnicos e materiais para implementar ou expandir projetos de impacto social.
        </Typography>
      </Box>

      {/* Benefícios dos Editais */}
      <Box sx={{ 
        display: 'grid', 
        gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' },
        gap: 3,
        px: 2
      }}>
        <Card sx={{ borderRadius: '16px' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <AttachMoneyIcon sx={{ fontSize: 48, color: '#009FE3', mb: 2 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#009FE3', 
                fontWeight: 'bold', 
                mb: 1,
                fontSize: {
                  xs: '1.4rem',  // Telas muito pequenas (320px-370px)
                  sm: '1.5rem',  // Telas pequenas até 600px
                  md: '1.6rem',  // Médias até 900px
                  lg: '1.8rem',  // Até 1200px
                  xl: '2rem',    // Telas grandes acima de 1200px
                },
                '@media (min-width: 900px) and (max-width: 945px)': {
                  fontSize: '1.3rem',
                },
                '@media (min-width: 946px) and (max-width: 1040px)': {
                  fontSize: '1.4rem',
                },
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'visible',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '3.6rem',
              }}
            >
              Financiamento
            </Typography>
            <Typography 
              variant="body1"
              sx={{
                fontSize: {
                  xs: '0.875rem', // Menor em telas muito pequenas
                  sm: '0.9rem',   // Pequeno em telas pequenas
                  md: '1rem'      // Tamanho normal em telas médias e grandes
                },
                lineHeight: 1.4   // Linha mais compacta
              }}
            >
              Acesso a recursos financeiros para projetos sociais
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: '16px' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <HandshakeIcon sx={{ fontSize: 48, color: '#009FE3', mb: 2 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#009FE3', 
                fontWeight: 'bold', 
                mb: 1,
                fontSize: {
                  xs: '1.4rem',  
                  sm: '1.5rem',
                  md: '1.6rem',  
                  lg: '1.8rem',
                  xl: '2rem',
                },
                '@media (min-width: 900px) and (max-width: 1040px)': {
                  fontSize: '1.4rem',
                },
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'visible',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '3.6rem',
              }}
            >
              Parcerias
            </Typography>
            <Typography 
              variant="body1"
              sx={{
                fontSize: {
                  xs: '0.875rem', // Menor em telas muito pequenas
                  sm: '0.9rem',   // Pequeno em telas pequenas
                  md: '1rem'      // Tamanho normal em telas médias e grandes
                },
                lineHeight: 1.4   // Linha mais compacta
              }}
            >
              Oportunidade de estabelecer parcerias com instituições públicas e privadas
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ borderRadius: '16px' }}>
          <CardContent sx={{ textAlign: 'center', py: 4 }}>
            <DescriptionIcon sx={{ fontSize: 48, color: '#009FE3', mb: 2 }} />
            <Typography 
              variant="h4" 
              sx={{ 
                color: '#009FE3', 
                fontWeight: 'bold', 
                mb: 1,
                fontSize: {
                  xs: '1.4rem',  
                  sm: '1.5rem',
                  md: '1.6rem',  
                  lg: '1.8rem',
                  xl: '2rem',
                },
                '@media (min-width: 900px) and (max-width: 1040px)': {
                  fontSize: '1.3rem',
                },
                lineHeight: 1.2,
                whiteSpace: 'nowrap',
                overflow: 'visible',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '3.6rem',
              }}
            >
              Credibilidade
            </Typography>
            <Typography 
              variant="body1"
              sx={{
                fontSize: {
                  xs: '0.875rem', // Menor em telas muito pequenas
                  sm: '0.9rem',   // Pequeno em telas pequenas
                  md: '1rem'      // Tamanho normal em telas médias e grandes
                },
                lineHeight: 1.4   // Linha mais compacta
              }}
            >
              Reconhecimento e legitimidade para sua organização e projetos
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Como participar */}
      <Box sx={{ textAlign: 'left', maxWidth: '800px', mx: 'auto' }}>
        <Typography variant="h4" component="h2" sx={{ 
          fontWeight: 'bold', 
          mb: 4,
          color: '#1B4B72'
        }}>
          Como participar de editais?
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          Para participar de editais, é importante que a organização esteja legalmente constituída, 
          com toda a documentação em dia. Cada edital possui critérios específicos, 
          mas geralmente solicitam:
        </Typography>
        <Box sx={{ textAlign: 'left', mb: 3, pl: 3 }}>
          <Typography component="ul" variant="body1">
            <li>CNPJ ativo e regularizado</li>
            <li>Estatuto social atualizado</li>
            <li>Certidões negativas de débitos</li>
            <li>Experiência prévia na área do projeto</li>
            <li>Projeto bem estruturado e alinhado com o edital</li>
          </Typography>
        </Box>
      </Box>

      {/* Call to Action - Prosas */}
      <Box id="find-notices" sx={scrollMarginStyle}>
        <Card sx={{ 
          borderRadius: '24px',
          border: '2px solid #009FE3'
        }}>
          <CardContent sx={{ px: 4, py: 6, textAlign: 'center' }}>
            <Typography variant="h4" component="h3" sx={{ fontWeight: 'bold', mb: 3, color: '#1B4B72' }}>
              Encontre editais ativos
            </Typography>
            <Typography variant="body1" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
              O Prosas é uma plataforma que reúne diversos editais de financiamento para 
              projetos sociais, culturais e ambientais. É uma excelente ferramenta para 
              encontrar oportunidades de captação de recursos para sua organização.
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="large"
              endIcon={<LaunchIcon />}
              href="https://prosas.com.br/editais"
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                borderRadius: '12px',
                px: 4,
                py: 1.5,
                fontWeight: 'bold',
                bgcolor: '#009FE3',
                '&:hover': { bgcolor: '#0084BE' }
              }}
            >
              Acessar o Prosas
            </Button>
            <Typography variant="body2" sx={{ mt: 3, color: '#666' }}>
              O Prosas é uma plataforma externa. O Bora Impactar não possui responsabilidade 
              sobre o conteúdo disponibilizado.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Stack>
  );
}